import type { ComponentType, ReactNode } from 'react'
import {
  AlertCircle,
  Bot,
  CheckCircle2,
  Clock,
  DollarSign,
  FileText,
  RotateCcw,
  Truck,
  User,
} from 'lucide-react'
import type {
  ProcessSubjectMessage,
  ProcessSubjectMessageIcon,
} from '../../../types/occurrences'
import type { DerivedEvent } from '../../../utils/thread-timeline'
import { formatDateTime } from '../../../utils/date-format'
import { describeDeadline } from '../../../utils/deadline'
import { linkifyText } from '../../../utils/linkify'
import { MessageAttachments } from './MessageAttachments'

const SYSTEM_ICONS: Record<
  ProcessSubjectMessageIcon,
  ComponentType<{ className?: string }>
> = {
  doc: FileText,
  truck: Truck,
  money: DollarSign,
  bot: Bot,
  alert: AlertCircle,
}

/** Cor do círculo do ícone, por tipo de evento. */
const ACCENT = {
  gray: 'bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-300',
  blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  amber: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  green: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  teal: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
  orange:
    'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  violet:
    'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
  red: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
} as const
export type EventAccent = keyof typeof ACCENT

/**
 * Evento da conversa (mudança de responsável, prazo, status…): pílula com o
 * ícone num círculo colorido por tipo, texto e "· data" — destacada no fundo
 * da conversa e diferente dos cards de mensagem. Alinhada aos cards.
 */
export function EventLine({
  icon: Icon,
  tone = 'neutral',
  accent,
  at,
  timeAsTooltip = false,
  messageId,
  children,
}: {
  icon: ComponentType<{ className?: string }>
  /** `danger`: a pílula inteira em vermelho (ex.: prazo vencido). */
  tone?: 'neutral' | 'danger' | 'success'
  /** Cor do ícone; padrão pela `tone` (verde / vermelho / cinza). */
  accent?: EventAccent
  at: string
  /** Data só ao passar o mouse (conversa do HUB, como na tela de
   * referência). */
  timeAsTooltip?: boolean
  messageId?: number
  children: ReactNode
}) {
  const danger = tone === 'danger'
  const iconColor =
    ACCENT[accent ?? (danger ? 'red' : tone === 'success' ? 'green' : 'gray')]
  return (
    <div
      data-message-id={messageId}
      title={timeAsTooltip ? formatDateTime(at) : undefined}
      className="ml-[54px] flex"
    >
      <span
        className={`inline-flex max-w-full items-start gap-2.5 rounded-2xl border py-1.5 pl-1.5 pr-4 text-[14px] leading-6 shadow-sm ${
          danger
            ? 'border-red-200 bg-red-50 text-red-800 dark:border-red-900/60 dark:bg-red-900/20 dark:text-red-300'
            : 'border-border-subtle bg-surface text-body/90'
        }`}
      >
        <span
          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${iconColor}`}
        >
          <Icon className="h-3.5 w-3.5" />
        </span>
        <span>
          {children}
          {!timeAsTooltip && (
            <span className={danger ? 'opacity-80' : 'text-muted'}>
              {' '}
              · {formatDateTime(at)}
            </span>
          )}
        </span>
      </span>
    </div>
  )
}

export const Strong = ({ children }: { children: ReactNode }) => (
  <strong className="font-semibold text-body">{children}</strong>
)

/** Evento deduzido (não gravado): responsável da abertura, prazo vencido. */
export function DerivedEventLine({ event }: { event: DerivedEvent }) {
  if (event.type === 'assigned') {
    return (
      <EventLine icon={User} accent="blue" at={event.at}>
        <Strong>{event.actor}</Strong> definiu <Strong>{event.target}</Strong>{' '}
        como responsável
      </EventLine>
    )
  }
  return (
    <EventLine icon={Clock} tone="danger" at={event.at}>
      {event.withoutUpdate ? 'Prazo venceu sem atualização' : 'Prazo venceu'}
    </EventLine>
  )
}

interface ChangePayload {
  status_change?: { from: string; to: string }
  responsible_change?: { to_id: number | null; to_name: string | null }
  deadline_change?: { from: string | null; to: string | null }
}

/**
 * Mensagem de sistema como linha de evento: finalizada/reaberta, troca de
 * responsável e de prazo (com quem fez), ou o texto gravado para as demais.
 */
export function SystemMessageLine({
  message,
  isOccurrence,
}: {
  message: ProcessSubjectMessage
  isOccurrence: boolean
}) {
  const p = (message.system_payload ?? {}) as ChangePayload
  const actor = message.author?.full_name
  const noun = isOccurrence ? 'a ocorrência' : 'o assunto'
  const at = message.created_at

  if (actor && p.status_change) {
    const done = p.status_change.to === 'done'
    return (
      <EventLine
        icon={done ? CheckCircle2 : RotateCcw}
        tone={done ? 'success' : 'neutral'}
        accent={done ? 'green' : 'orange'}
        at={at}
        messageId={message.id}
      >
        <Strong>{actor}</Strong> {done ? 'finalizou' : 'reabriu'} {noun}
      </EventLine>
    )
  }
  if (actor && p.responsible_change) {
    const to = p.responsible_change.to_name
    return (
      <EventLine icon={User} accent="blue" at={at} messageId={message.id}>
        <Strong>{actor}</Strong>{' '}
        {to ? (
          <>
            definiu <Strong>{to}</Strong> como responsável
          </>
        ) : (
          'removeu o responsável'
        )}
      </EventLine>
    )
  }
  if (actor && p.deadline_change) {
    const to = p.deadline_change.to
    return (
      <EventLine icon={Clock} accent="amber" at={at} messageId={message.id}>
        <Strong>{actor}</Strong>{' '}
        {to ? (
          <>
            alterou o prazo para{' '}
            <Strong>{describeDeadline(to, true).date}</Strong>
          </>
        ) : (
          'removeu o prazo'
        )}
      </EventLine>
    )
  }

  const Icon = message.system_icon ? SYSTEM_ICONS[message.system_icon] : Bot
  return (
    <div data-message-id={message.id}>
      <EventLine icon={Icon} at={at}>
        <span className="whitespace-pre-wrap">
          {linkifyText(message.content)}
        </span>
      </EventLine>
      <div className="ml-[94px]">
        <MessageAttachments message={message} />
      </div>
    </div>
  )
}
